import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Box,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Security as ShieldIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { deleteRole, getRoles, updateRole } from "../utils/roleApi";
import { toast } from "react-toastify";
import { MenuPermission } from "../utils/menuPermissions";
import { usePermissions } from "../../../hooks/usePermissions";

const ROLE_DESCRIPTION_DE: Record<string, string> = {
  "Administrator role with full access": "Administrator-Rolle mit Vollzugriff",
  "Chatbot role for Concierge team": "Chatbot-Rolle für Concierge-Team",
};

interface Role {
  _id?: string;
  name: string;
  displayText: string;
  isActive: boolean;
  permissions?: MenuPermission[];
  createdAt?: string;
  updatedAt?: string;
}

interface RoleListMUIProps {
  onEditRole: (role: Role) => void;
  onRoleUpdated: () => void;
  refreshTrigger?: number;
}

// Helper function to get role chip sx object based on role name
const getRoleChipSx = (roleName: string): object => {
  const normalizedName = roleName.toLowerCase();

  if (normalizedName === "superadmin") {
    return {
      backgroundColor: "#9c27b0",
      color: "#fff",
      fontWeight: 600,
    };
  } else if (normalizedName === "admin") {
    return {
      backgroundColor: "#2196f3",
      color: "#fff",
      fontWeight: 600,
    };
  } else if (normalizedName === "chatbot") {
    return {
      backgroundColor: "#ff9800",
      color: "#fff",
      fontWeight: 600,
    };
  } else if (normalizedName === "user") {
    return {
      backgroundColor: "#757575",
      color: "#fff",
      fontWeight: 600,
    };
  }

  return {
    backgroundColor: "#607d8b",
    color: "#fff",
    fontWeight: 600,
  };
};

export const RoleListMUI: React.FC<RoleListMUIProps> = ({
  onEditRole,
  onRoleUpdated,
  refreshTrigger = 0,
}) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    roleId?: string;
    roleName?: string;
  }>({ open: false });

  // Permission checks for role management
  const { canView, canEdit, canDelete } = usePermissions('/role-management');

  const fetchRoles = async (currentPage: number = 0, search: string = "") => {
    try {
      setLoading(true);
      const response = await getRoles({
        page: currentPage + 1, // Backend uses 1-based pagination
        limit: rowsPerPage,
        ...(search && { letter: search }),
      });

      if (response.data.success) {
        const rolesData = Array.isArray(response.data.data) ? response.data.data : [];
        // Transform backend data to frontend format
        const transformedRoles = rolesData.map((role: any) => ({
          _id: role._id,
          name: role.name,
          displayText: ROLE_DESCRIPTION_DE[role.displayText] || role.displayText,
          isActive: role.activeFlag,
          permissions: role.permissions || [],
          createdAt: role.createdAt,
          updatedAt: role.updatedAt,
        }));
        setRoles(transformedRoles);
        setTotalCount(response.data.count || 0);
      } else {
        toast.error("Fehler beim Laden der Rollen");
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Fehler beim Laden der Rollen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles(page, debouncedSearch);
  }, [page, rowsPerPage, debouncedSearch, refreshTrigger]);

  const handleDelete = (roleId: string, roleName: string) => {
    setConfirmDialog({ open: true, roleId, roleName });
  };

  const handleConfirmDelete = async () => {
    const { roleId } = confirmDialog;
    setConfirmDialog({ open: false });

    if (!roleId) return;

    try {
      const response = await deleteRole(roleId);

      if (response.data.success) {
        toast.success("Rolle erfolgreich gelöscht");
        fetchRoles(page, searchTerm);
        onRoleUpdated();
      } else {
        toast.error(response.data.message || "Fehler beim Löschen der Rolle");
      }
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error("Fehler beim Löschen der Rolle");
    }
  };

  const handleToggleStatus = async (roleId: string, currentStatus: boolean, roleName: string) => {
    try {
      const response = await updateRole({
        id: roleId,
        isActive: !currentStatus,
      });

      if (response.data.success) {
        toast.success(
          `Rolle "${roleName}" wurde ${!currentStatus ? "aktiviert" : "deaktiviert"}`
        );
        fetchRoles(page, searchTerm);
        onRoleUpdated();
      } else {
        toast.error(response.data.message || "Fehler beim Ändern des Rollenstatus");
      }
    } catch (error) {
      console.error("Error toggling role status:", error);
      toast.error("Fehler beim Ändern des Rollenstatus");
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 400);
  };

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  return (
    <Box>
      <TextField
        sx={{ mb: 3, maxWidth: 400 }}
        variant="outlined"
        placeholder="Rollen suchen..."
        value={searchTerm}
        onChange={handleSearchChange}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 8,
          }}
        >
          <CircularProgress />
        </Box>
      ) : roles.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            padding: 8,
            color: "text.secondary",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Keine Rollen gefunden
          </Typography>
          <Typography variant="body2">
            {searchTerm
              ? "Versuchen Sie einen anderen Suchbegriff"
              : "Erstellen Sie Ihre erste Rolle"}
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rolle</TableCell>
                  <TableCell>Beschreibung</TableCell>
                  <TableCell align="center">Berechtigungen</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Erstellt am</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role._id} hover>
                    <TableCell>
                      <Chip
                        label={role.name}
                        size="small"
                        sx={getRoleChipSx(role.name)}
                      />
                    </TableCell>
                    <TableCell>{role.displayText}</TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<ShieldIcon sx={{ color: "#fff !important" }} />}
                        label={`${role.permissions?.length || 0} Menüpunkt(e)`}
                        size="small"
                        color="default"
                        sx={{
                          bgcolor: "#546e7a",
                          color: "#fff",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={role.isActive ? "Aktiv" : "Inaktiv"}
                        size="small"
                        color={role.isActive ? "success" : "default"}
                        sx={role.isActive ? { fontWeight: 500 } : { fontWeight: 500, bgcolor: "#9e9e9e", color: "#fff" }}
                      />
                    </TableCell>
                    <TableCell>
                      {role.createdAt
                        ? new Date(role.createdAt).toLocaleDateString("de-DE")
                        : "-"}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        {canEdit && (
                          <Tooltip
                            title={role.isActive ? "Deaktivieren" : "Aktivieren"}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                role._id &&
                                handleToggleStatus(role._id, role.isActive, role.name)
                              }
                              color={role.isActive ? "warning" : "success"}
                            >
                              {role.isActive ? <CancelIcon /> : <CheckCircleIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEdit && (
                          <Tooltip title="Bearbeiten">
                            <IconButton
                              size="small"
                              onClick={() => onEditRole(role)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Löschen">
                            <IconButton
                              size="small"
                              onClick={() => role._id && handleDelete(role._id, role.name)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!canEdit && !canDelete && (
                          <Typography variant="caption" color="textSecondary">
                            Keine Berechtigungen
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
            }
          />
        </>
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false })}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Rolle löschen?
          <IconButton size="small" onClick={() => setConfirmDialog({ open: false })} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Möchten Sie die Rolle "${confirmDialog.roleName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false })} color="inherit">
            Abbrechen
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
